<template>
  <div>
    <slot name="list-header"></slot>
    <template v-if="hasFetchEnd && data.length === 0">
      <slot name="list-empty">
        <div v-if="emptyHint" class="m-3 rounded-md font-size-md text-center" :class="emptyHint.class">
          <div class="image-panel image-panel-5 bg-none mt-5 mb-2">
            <img :src="emptyHint.img" alt="">
          </div>
          <p class="text-mcf-color">{{ emptyHint.desc }}</p>
          <b-button v-if="emptyHint.button" @click="emptyHint.button.onClick" variant="success" class="px-4 bg-customize border-0 rounded-pill">{{ emptyHint.button.text }}</b-button>
        </div>
      </slot>
    </template>
    <template v-else>
      <template v-for="(item, index) in data">
          <slot name="list-item" :item="item" :index="index" />
      </template>
      <template v-if="isFetching && spinner">
        <div class="d-flex justify-content-center mb-3">
          <b-spinner class="m-3" :variant="spinner" label="Loading..."></b-spinner>
        </div>
      </template>
    </template>
    <slot name="list-footer"></slot>
  </div>
</template>
<script>
import {
  defer, fromEvent, interval, empty,
} from 'rxjs';
import {
  throttle, map, filter, tap, finalize, catchError,
} from 'rxjs/operators';

const findContainer = function findContainer(element) {
  while (element) {
    if (element.$refs.scrollContainer) {
      return element.$refs.scrollContainer.$el ?? element.$refs.scrollContainer;
    }
    element = element.$parent;
  }

  console.warn('None of container ref assigned as "scrollContainer" found, "window" is used as the container.');
  return window;
};

export default {
  props: {
    // the data key for v-for :key
    itemKey: {
      type: String,
      default: 'id',
    },
    // margin height (px)
    scrollMargin: {
      type: Number,
      default: 300,
    },
    // fetch limit per request
    limit: {
      type: Number,
      default: 10,
    },
    // default page
    page: {
      type: Number,
      default: 1,
    },
    // fetch function, must return a observable object
    fetch: {
      type: Function,
      required: true,
    },
    // spinner type, won't show if not set
    spinner: {
      type: String,
    },
    // empty hint object
    emptyHint: {
      type: Object,
      default() {
        return {
          img: './assets/images/illustration/undraw_shopping_app_flsj.svg',
          desc: 'No result.',
          button: {
            text: 'button',
            onClick() { return; },
          },
        };
      },
    },
    // filter object
    filter: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    const vm = this;

    return {
      isFetching: false,
      hasFetchEnd: false,
      pagination: {
        limit: vm.limit,
        page: vm.page,
        get offset() {
          return (this.page - 1) * this.limit;
        },
      },
      data: [],
    };
  },
  mounted() {
    const vm = this;
    // scroll event observable
    const scrollContainer = findContainer(vm);
    const scrollObservable = fromEvent(scrollContainer, 'scroll').pipe(
      throttle(() => interval(50)),
      filter(() => !vm.hasFetchEnd && !vm.isFetching),
      map((res) => {
        let el = res.target;
        if (el === document) el = document.scrollingElement;
        return el.scrollTop + el.clientHeight + vm.scrollMargin >= el.scrollHeight;
      }),
      filter((res) => res),
      tap(() => {
        vm.subscription = vm.$subscribeTo(vm.fetch$());
      }),
    );
    vm.$subscribeTo(scrollObservable);
  },
  subscriptions() {
    const vm = this;
    const filter$ = vm.$watchAsObservable('filter', { deep: true, immediate: true }).pipe(
      tap(() => {
        if (vm.subscription && !vm.subscription.closed) {
          vm.subscription.unsubscribe();
        }
        vm.subscription = vm.$subscribeTo(vm.initialize$());
      }),
    );

    return {
      filter$,
    };
  },
  methods: {
    // initialize data
    initialize$() {
      const vm = this;

      return defer(() => {
        vm.pagination.page = 1;
        vm.data = [];
        vm.hasFetchEnd = false;

        return vm.fetch$().pipe(
          tap(() => vm.$nextTick(() => {
            vm.$emit('initialized', vm.data);
          })),
        );
      });
    },
    // fetch data
    fetch$() {
      const vm = this;
      if (vm.fetch) {
        return defer(() => {
          vm.isFetching = true;
          return vm.fetch(vm.pagination, vm.filter).pipe(
            tap((response) => {
              const res = response.list;
              if (res && res.length === 0) {
                vm.hasFetchEnd = true;
                if (vm.data.length !== 0) vm.$emit('reachBottom');
              } else if (res && res.length < vm.limit) {
                vm.hasFetchEnd = true;
                vm.$emit('reachBottom');
                vm.pagination.page += 1;
              } else if (res) {
                vm.pagination.page += 1;
              }
              vm.data = vm.data.concat(res);
            }),
            catchError((err) => {
              console.error(err);
            }),
            finalize(() => {
              vm.isFetching = false;
            }),
          );
        });
      }

      return empty();
    },
  },
};
</script>
